import { FieldProvenanceDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { SparklesIcon } from 'lucide-react';
import { FC } from 'react';

type FieldProvenanceBadgeProps = {
  className?: string;
  /**
   * `detail` (default) — read-only views; tooltip reads "AI-assisted · <date>".
   * `form` — while editing; tooltip explains the field will be marked AI-assisted
   * once the assessment is saved.
   */
  context?: 'detail' | 'form';
  provenance?: FieldProvenanceDto | null;
};

const FORM_TOOLTIP = 'This field was filled in with AI. It’ll be marked “AI-assisted” once you save the assessment.';

const formatGeneratedAt = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Marks a field/item as AI-assisted (ADR-0007): a subtle gray outline sparkle
 * chip; the label and date live in the tooltip. Renders nothing when provenance
 * is absent (human-authored).
 */
export const FieldProvenanceBadge: FC<FieldProvenanceBadgeProps> = (props) => {
  const { className, context = 'detail', provenance } = props;

  if (!provenance) {
    return null;
  }

  const generatedAt = formatGeneratedAt(provenance.generatedAt);
  const tooltip = context === 'form' ? FORM_TOOLTIP : generatedAt ? `AI-assisted · ${generatedAt}` : 'AI-assisted';

  const badge = (
    <Badge
      aria-label="AI-assisted"
      className={cn('border-gray-300 bg-transparent px-1 text-gray-500', className)}
      size="xs"
      variant="outline"
    >
      <SparklesIcon className="size-3 shrink-0" />
    </Badge>
  );

  return <TooltipComponent trigger={badge}>{tooltip}</TooltipComponent>;
};
